import type { PurchaseItem } from '../types/purchase';
import type { PurchaseReceiptItem, PurchaseReceiptItemInput } from '../types/purchaseReceipt';
import type { WarehouseGoodsItem } from '../types/warehouseGoodsCatalog';

export type LegacyPurchaseReceiptCandidateState =
  | 'eligible'
  | 'already_imported'
  | 'not_cukcuk_store'
  | 'transfer'
  | 'invalid_number'
  | 'missing_material'
  | 'ambiguous_material'
  | 'missing_cukcuk_mapping'
  | 'unit_mismatch';

export interface LegacyPurchaseReceiptCandidate {
  purchase: PurchaseItem;
  material: WarehouseGoodsItem | null;
  state: LegacyPurchaseReceiptCandidateState;
  reason: string;
}

export interface BuildLegacyPurchaseReceiptCandidatesInput {
  purchases: PurchaseItem[];
  materials: WarehouseGoodsItem[];
  cukcukStoreIds: ReadonlySet<string>;
  importedPurchaseIds: ReadonlySet<string>;
}

const normalizeMatchKey = (value: unknown): string => String(value || '')
  .normalize('NFD')
  .replace(/[\u0300-\u036f]/g, '')
  .toLocaleLowerCase('vi')
  .replace(/[^a-z0-9]/g, '');

const normalizedId = (value: unknown): string => String(value || '').trim();

const isFiniteNonNegative = (value: unknown): value is number =>
  typeof value === 'number' && Number.isFinite(value) && value >= 0;

const isTransfer = (purchase: PurchaseItem): boolean => Boolean(
  purchase.fromWarehouse || purchase.isTransferIn || purchase.isTransferOut,
);

const makeCandidate = (
  purchase: PurchaseItem,
  material: WarehouseGoodsItem | null,
  state: LegacyPurchaseReceiptCandidateState,
  reason: string,
): LegacyPurchaseReceiptCandidate => ({ purchase, material, state, reason });

/**
 * Matches legacy independent purchase rows to CUKCUK-exportable receipt lines.
 * This intentionally only performs exact normalized-name matching; it does not
 * infer an ingredient, a unit conversion, or a purchase-receipt grouping.
 */
export const buildLegacyPurchaseReceiptCandidates = ({
  purchases,
  materials,
  cukcukStoreIds,
  importedPurchaseIds,
}: BuildLegacyPurchaseReceiptCandidatesInput): LegacyPurchaseReceiptCandidate[] => {
  const materialByName = new Map<string, WarehouseGoodsItem[]>();
  materials.forEach((material) => {
    const key = normalizeMatchKey(material.name);
    if (!key) return;
    materialByName.set(key, [...(materialByName.get(key) || []), material]);
  });

  return purchases.map((purchase) => {
    const sourceId = normalizedId(purchase.id);
    const storeId = normalizedId(purchase.storeLocationId);
    const matchingMaterials = materialByName.get(normalizeMatchKey(purchase.itemName)) || [];
    const purchaseUnitKey = normalizeMatchKey(purchase.unit);
    const exactUnitMaterials = purchaseUnitKey
      ? matchingMaterials.filter((candidate) =>
        Boolean(candidate.cukcukUnit) && normalizeMatchKey(candidate.cukcukUnit) === purchaseUnitKey,
      )
      : [];
    const material = matchingMaterials.length === 1
      ? matchingMaterials[0]
      : exactUnitMaterials.length === 1
        ? exactUnitMaterials[0]
        : null;

    if (!sourceId) {
      return makeCandidate(purchase, material, 'invalid_number', 'Dòng mua cũ thiếu mã nguồn');
    }
    if (importedPurchaseIds.has(sourceId)) {
      return makeCandidate(purchase, material, 'already_imported', 'Đã được đưa vào một phiếu nhập');
    }
    if (!storeId || !cukcukStoreIds.has(storeId)) {
      return makeCandidate(purchase, material, 'not_cukcuk_store', 'Cơ sở này chưa dùng CUKCUK');
    }
    if (isTransfer(purchase)) {
      return makeCandidate(purchase, material, 'transfer', 'Đây là dòng chuyển kho, không phải nhập mua');
    }
    if (!isFiniteNonNegative(purchase.quantity) || purchase.quantity <= 0 || !isFiniteNonNegative(purchase.unitPrice)) {
      return makeCandidate(purchase, material, 'invalid_number', 'Số lượng hoặc đơn giá không hợp lệ');
    }
    if (matchingMaterials.length > 1 && exactUnitMaterials.length !== 1) {
      return makeCandidate(purchase, null, 'ambiguous_material', 'Có nhiều nguyên liệu cùng tên; cần xác nhận mã CUKCUK');
    }
    if (!material) {
      return makeCandidate(purchase, null, 'missing_material', 'Không có nguyên liệu khớp chính xác trong catalog 8am');
    }
    if (!material.cukcukMaterialCode || !material.cukcukUnit) {
      return makeCandidate(purchase, material, 'missing_cukcuk_mapping', 'Nguyên liệu chưa có mã hoặc ĐVT CUKCUK');
    }
    if (normalizeMatchKey(purchase.unit) !== normalizeMatchKey(material.cukcukUnit)) {
      return makeCandidate(
        purchase,
        material,
        'unit_mismatch',
        `ĐVT dữ liệu cũ (${purchase.unit || 'trống'}) khác ĐVT CUKCUK (${material.cukcukUnit})`,
      );
    }

    return makeCandidate(purchase, material, 'eligible', 'Sẵn sàng tạo phiếu nháp để rà soát');
  });
};

/** Converts only explicitly selected, eligible candidates into draft receipt items. */
export const toLegacyPurchaseReceiptItems = (
  candidates: LegacyPurchaseReceiptCandidate[],
): PurchaseReceiptItemInput[] => candidates.map((candidate) => {
  const { purchase, material } = candidate;
  const selectable = candidate.state === 'eligible' || candidate.state === 'already_imported';
  if (!selectable || !material?.id || !material.cukcukUnit || !purchase.id) {
    throw new Error('Legacy purchase candidate is not eligible');
  }

  return {
    materialId: material.id,
    quantity: purchase.quantity,
    unit: material.cukcukUnit,
    unitPrice: purchase.unitPrice,
    note: purchase.description?.trim() || null,
    sourcePurchaseId: purchase.id,
  };
});

/** Groups selected legacy rows into CUKCUK-safe receipt boundaries. */
export const groupLegacyPurchaseReceiptCandidates = (
  candidates: LegacyPurchaseReceiptCandidate[],
): LegacyPurchaseReceiptCandidate[][] => Array.from(
  candidates.reduce((groups, candidate) => {
    const purchase = candidate.purchase;
    const dateKey = purchase.date.toISOString().slice(0, 10);
    const supplierKey = `${normalizedId(purchase.supplierId)}|${String(purchase.supplierSource || '').trim()}`;
    const key = `${dateKey}|${normalizedId(purchase.storeLocationId)}|${supplierKey}`;
    groups.set(key, [...(groups.get(key) || []), candidate]);
    return groups;
  }, new Map<string, LegacyPurchaseReceiptCandidate[]>()),
).map(([, group]) => group);

export interface ReceiptSupplierSummary {
  suppliers: Array<{ supplierId: string; supplierName: string }>;
  missingMaterialCount: number;
}

/** Summarizes mapped suppliers without choosing one for a mixed receipt. */
export const summarizeReceiptSuppliers = (
  items: PurchaseReceiptItem[],
  materials: WarehouseGoodsItem[],
): ReceiptSupplierSummary => {
  const materialsById = new Map(materials.map((material) => [material.id, material]));
  const suppliers = new Map<string, { supplierId: string; supplierName: string }>();
  let missingMaterialCount = 0;

  for (const item of items) {
    const material = materialsById.get(item.materialId);
    const supplierId = material?.supplierId?.trim();
    const supplierName = material?.supplierName?.trim();
    if (!supplierId || !supplierName) {
      missingMaterialCount += 1;
      continue;
    }
    const key = `${supplierId}|${supplierName}`;
    suppliers.set(key, { supplierId, supplierName });
  }

  return { suppliers: Array.from(suppliers.values()), missingMaterialCount };
};

/**
 * Resolves a single supplier for an existing receipt without guessing.
 * Every stored line must point to a catalog material with the same supplier;
 * mixed or incomplete supplier data intentionally returns null.
 */
export const resolveReceiptSupplier = (
  items: PurchaseReceiptItem[],
  materials: WarehouseGoodsItem[],
): { supplierId: string; supplierName: string } | null => {
  const summary = summarizeReceiptSuppliers(items, materials);
  return summary.missingMaterialCount === 0 && summary.suppliers.length === 1
    ? summary.suppliers[0]
    : null;
};
